import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useGetMatchResults,
  useIsSemanticMatching,
  useSemanticMatchWithProgress,
  useSemanticMatchProfile,
  type MatchProgress,
} from "../../../../../services/organtization/semantic-match";
import { organizationProfilesQueryOptions } from "../../../../../services/organtization/get-organization-profiles";
import { LoadingSpinner, Image, Tooltip, TooltipContent, TooltipTrigger } from "../../../../../components";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Button } from "../../../../../components/buttons/button";
import { getImageUrl } from "../../../../../utils/image";
import EditorSkillTagButton from "../../../../../components/atoms/EditorSkillTagButton";
import type { ActiveContract, Lead, UserProfile } from "../../../../../client";
import type { SemanticProfileMatch } from "../../../../../client";
import { cn } from "../../../../../components/utils/cn";
import MagicIcon from "../../../../../assets/magic_icon.png";
import { homeState } from "../../../../../state/home";
import { AvailabilityTag, ContractTag } from "../AvailabilityPill";

const REVEAL_DURATION_MS = 1800;

const RoleReveal = ({
  title,
  onDone,
}: {
  title: string;
  onDone: () => void;
}) => {
  useEffect(() => {
    const timer = setTimeout(onDone, REVEAL_DURATION_MS);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <div className="flex flex-col justify-center items-center rounded-2xl relative overflow-hidden h-full min-h-[400px]">
      <style>
        {`
          @keyframes matchGradientShift {
            0% { background-position: 0% 50%; }
            100% { background-position: 100% 50%; }
          }
          @keyframes revealFadeIn {
            0% { opacity: 0; transform: translateY(12px); }
            30% { opacity: 1; transform: translateY(0); }
            85% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-8px); }
          }
        `}
      </style>
      <div
        className="absolute inset-0 rounded-2xl"
        style={{
          background:
            "linear-gradient(90deg, rgba(57,220,234,0.05) 10%, rgba(255,40,166,0.05) 88%)",
          backgroundSize: "200% 100%",
          animation: "matchGradientShift 3s ease-in-out infinite alternate",
        }}
      />
      <div className="flex flex-col items-center gap-3 relative z-10">
        <div className="flex flex-row gap-1.5 items-center">
          <img src={MagicIcon} alt="" className="w-6 h-6" />
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
            Assignment parsed
          </span>
        </div>
        <h2
          className="font-bold text-gray-900 text-xl text-center px-8"
          style={{
            animation: `revealFadeIn ${REVEAL_DURATION_MS}ms ease-in-out forwards`,
          }}
        >
          {title}
        </h2>
      </div>
    </div>
  );
};

interface Props {
  organizationId: string;
  leadId: string;
  onMutateLead: (lead: Partial<Lead>) => void;
  childLeads?: Lead[];
  isParsingAssignment?: boolean;
  hideAction?: boolean;
  hasAssignment?: boolean;
  revealTitle?: string;
  onRevealDone?: () => void;
  matchingProfileIds?: string[];
  isProfilePage?: boolean;
  externalIsMatching?: boolean;
  matchProgress?: MatchProgress;
}

const MATCHING_WORDS = ["skills", "domains", "experience", "fit"];
const PARSING_WORDS = ["workplace", "skills", "domains", "must haves"];

interface MatchingLoadingStateProps {
  phase?: "parsing" | "matching";
  subtitle?: string;
  progress?: MatchProgress;
}

export const MatchingLoadingState = ({
  phase = "matching",
  subtitle: subtitleOverride,
  progress,
}: MatchingLoadingStateProps) => {
  const words = phase === "parsing" ? PARSING_WORDS : MATCHING_WORDS;
  const label = phase === "parsing" ? "Understanding" : "Matching";
  const subtitle =
    subtitleOverride ??
    (phase === "parsing"
      ? "Reading assignment details"
      : "Looking for matching candidates");

  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  useEffect(() => {
    let currentIndex = 0;
    setCurrentWordIndex(0);
    let interval: NodeJS.Timeout;

    const initialTimeout = setTimeout(() => {
      interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % words.length;
        setCurrentWordIndex(currentIndex);
      }, 2000);
    }, 100);

    return () => {
      clearTimeout(initialTimeout);
      if (interval) clearInterval(interval);
    };
  }, [words.length]);

  return (
    <div className="flex flex-col justify-center items-center rounded-2xl relative overflow-hidden h-full min-h-[400px]">
      <style>
        {`
          @keyframes matchGradientShift {
            0% { background-position: 0% 50%; }
            100% { background-position: 100% 50%; }
          }
          @keyframes matchFadeInOut {
            0%, 20% { opacity: 0; transform: translateY(10px); }
            30%, 70% { opacity: 1; transform: translateY(0); }
            80%, 100% { opacity: 0; transform: translateY(-10px); }
          }
        `}
      </style>
      <div
        className="absolute inset-0 rounded-2xl"
        style={{
          background:
            "linear-gradient(90deg, rgba(57,220,234,0.05) 10%, rgba(255,40,166,0.05) 88%)",
          backgroundSize: "200% 100%",
          animation: "matchGradientShift 3s ease-in-out infinite alternate",
        }}
      />
      <div className="flex flex-row gap-1.5 relative z-10 items-center">
        <img src={MagicIcon} alt="" className="w-6 h-6" />
        <h2 className="font-bold text-gray-6 flex items-center gap-1">
          <span>{label}</span>
          <span
            className="inline-block text-left"
            style={{ width: "110px", minWidth: "110px" }}
          >
            <span
              key={currentWordIndex}
              className="inline-block"
              style={{
                animation: "matchFadeInOut 2s ease-in-out infinite",
              }}
            >
              {words[currentWordIndex]}
            </span>
          </span>
        </h2>
      </div>
      <p className="text-xs text-gray-6 relative z-10 mt-4">{subtitle}</p>
      {phase === "matching" && progress && (() => {
        const steps = 1 + (progress.totalProfiles || 4);
        const completed = (progress.embeddingDone ? 1 : 0) + progress.profilesScored;
        const pct = Math.round((completed / steps) * 100);
        return (
          <div className="flex flex-col items-center gap-1 relative z-10 mt-3">
            <div className="h-1.5 w-48 bg-gray-300 rounded-full overflow-hidden">
              <div
                className="h-full bg-gray-900 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-[10px] text-gray-400">{pct}%</p>
          </div>
        );
      })()}
    </div>
  );
};

export const SemanticMatchResultsList = ({
  organizationId,
  leadId,
  onMutateLead,
  childLeads,
  isParsingAssignment,
  hideAction,
  hasAssignment,
  revealTitle,
  onRevealDone,
  matchingProfileIds,
  isProfilePage,
  externalIsMatching,
  matchProgress,
}: Props) => {
  const [matchingProfileId, setMatchingProfileId] = useState<string | null>(
    null,
  );
  const assignedProfileIds = new Set(
    (childLeads ?? []).map((l) => l.profileId).filter(Boolean),
  );
  const isProfileContext = isProfilePage ?? false;
  const { data, isLoading, error } = useGetMatchResults(organizationId, leadId);
  const {
    run: runMatch,
    isMatching: isSseMatching,
    progress: sseProgress,
  } = useSemanticMatchWithProgress(organizationId, leadId);
  const semanticMatchProfile = useSemanticMatchProfile({
    onSettled: () => setMatchingProfileId(null),
  });
  const isMatchingInternal = useIsSemanticMatching(leadId);
  const isMatching = isSseMatching || externalIsMatching || isMatchingInternal;
  const activeProgress = isSseMatching ? sseProgress : (externalIsMatching ? matchProgress : undefined);
  const { data: orgProfiles, isLoading: orgProfilesLoading } = useQuery({
    ...organizationProfilesQueryOptions(organizationId),
  });

  if (revealTitle && onRevealDone) {
    return <RoleReveal title={revealTitle} onDone={onRevealDone} />;
  }

  const isMatchingProfiles = (matchingProfileIds ?? []).length > 0;
  const matchingProfileSubtitle = isMatchingProfiles
    ? (() => {
        const names = (matchingProfileIds ?? [])
          .map((id) => (orgProfiles ?? []).find((p) => p.profileId === id))
          .filter(Boolean)
          .map((p) =>
            typeof p?.profileData?.name === "string"
              ? p.profileData.name
              : null,
          )
          .filter(Boolean)
          .join(", ");
        return names ? `Matching with ${names}` : "Matching with profile";
      })()
    : undefined;

  if (isLoading || isMatching || isParsingAssignment || isMatchingProfiles) {
    return (
      <MatchingLoadingState
        phase={isParsingAssignment ? "parsing" : "matching"}
        subtitle={matchingProfileSubtitle}
        progress={activeProgress}
      />
    );
  }

  const results = data?.results;

  if (error || !results || results.length === 0) {
    if (!isProfileContext && hasAssignment) {
      return (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">
              Do you want to match this assignment with other candidates?
            </p>
            <div className="flex items-center gap-2">
              {!hideAction && (
                <Button variant="magic" onClick={runMatch}>
                  Run matching
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => {
                  homeState.assignmentDrawerLeadId = leadId;
                }}
              >
                Show assignment info
              </Button>
            </div>
          </div>
        </div>
      );
    }

    const profiles = (orgProfiles ?? []).filter(
      (p) => !assignedProfileIds.has(p.profileId) && p.role !== "default",
    );
    return (
      <div className="flex flex-col gap-2 overflow-y-auto max-h-[80vh] pr-1">
        {!isProfileContext && (
          <div className="flex items-center justify-between">
            <span className="font-bold text-lg">
              Select profiles for the assignment
            </span>
            {!hideAction && (
              <Button variant="magic" onClick={runMatch}>
                Run matching
              </Button>
            )}
          </div>
        )}
        {!isProfileContext && (
          <div className="grid grid-cols-2 gap-5">
            {profiles.map((profile) => (
              <OrgMemberCard
                key={profile.profileId}
                profile={profile}
                onAssign={() => onMutateLead({ profileId: profile.profileId })}
                onMatch={
                  hasAssignment && profile.profileId
                    ? () => {
                        setMatchingProfileId(profile.profileId!);
                        semanticMatchProfile.mutate({
                          path: {
                            organizationID: organizationId,
                            profileID: profile.profileId!,
                          },
                          body: { leadId },
                        });
                      }
                    : undefined
                }
                isMatching={matchingProfileId === profile.profileId}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  const sorted = [...results]
    .filter((m) => !assignedProfileIds.has(m.profileId ?? ""))
    .filter((m) => !((!m.domainScore || m.domainScore === 0) && !m.fitLevel))
    .sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0));

  if (!isProfileContext && sorted.length === 0 && assignedProfileIds.size > 0 && results.length <= 1) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-400">
            Run the matching to find more candidates for this lead
          </p>
          <div className="flex items-center gap-2">
            {!hideAction && (
              <Button variant="magic" onClick={runMatch}>
                Run matching
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => {
                homeState.assignmentDrawerLeadId = leadId;
              }}
            >
              Show assignment info
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const matchedProfileIds = new Set(results.map((m) => m.profileId ?? ""));
  const remainingProfiles = (orgProfiles ?? []).filter(
    (p) =>
      !matchedProfileIds.has(p.profileId) &&
      !assignedProfileIds.has(p.profileId) &&
      p.role !== "default",
  );

  return (
    <div className="flex flex-col gap-1 overflow-y-auto max-h-[80vh] pr-1">
      {!isProfileContext && (
        <span className="font-bold text-lg">
          Select profiles for the assignment
        </span>
      )}
      <div className="flex items-center justify-between">
        {!isProfileContext && (
          <p className="text-xs text-gray-400">Best matches</p>
        )}
        <div className="flex items-center gap-2">
          {!hideAction && !isProfileContext && data?.updatedAt && (
            <p className="text-xs text-gray-400">
              Last run:{" "}
              {new Date(data.updatedAt).toLocaleString(undefined, {
                dateStyle: "short",
                timeStyle: "short",
              })}
            </p>
          )}
          {!hideAction && !isProfileContext && (
            <Button variant="magic" onClick={runMatch}>
              Re-run matching
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              homeState.assignmentDrawerLeadId = leadId;
            }}
          >
            Show assignment info
          </Button>
        </div>
      </div>
      {!isProfileContext && (
        <div className="grid grid-cols-2 gap-4">
          {sorted.map((match) => {
            const profile = (orgProfiles ?? []).find(
              (p) => p.profileId === match.profileId,
            );
            const profileTitle =
              typeof profile?.profileData?.title === "string"
                ? profile.profileData.title
                : undefined;
            return (
              <MatchResultCard
                key={match.profileId}
                match={match}
                profileTitle={profileTitle}
                availabilityDate={profile?.availabilityDate}
                activeContract={profile?.activeContract}
                profileLoaded={!orgProfilesLoading}
                onAssign={() =>
                  onMutateLead({ profileId: match.profileId ?? undefined })
                }
              />
            );
          })}
        </div>
      )}
      {!isProfileContext && remainingProfiles.length > 0 && (
        <>
          <p className="text-xs text-gray-400 mt-6">Other profiles</p>
          <div className="grid grid-cols-4 gap-5 mb-6">
            {remainingProfiles.map((profile) => (
              <OrgMemberCard
                key={profile.profileId}
                profile={profile}
                onAssign={() => onMutateLead({ profileId: profile.profileId })}
                onMatch={
                  profile.profileId
                    ? () => {
                        setMatchingProfileId(profile.profileId!);
                        semanticMatchProfile.mutate({
                          path: {
                            organizationID: organizationId,
                            profileID: profile.profileId!,
                          },
                          body: { leadId },
                        });
                      }
                    : undefined
                }
                isMatching={matchingProfileId === profile.profileId}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

interface CardProps {
  match: SemanticProfileMatch;
  onAssign: () => void;
  profileTitle?: string;
  availabilityDate?: string | null;
  activeContract?: ActiveContract | null;
  profileLoaded?: boolean;
}

const scoreEmoji = (score: number) => {
  if (score >= 80) return "🎯";
  if (score >= 65) return "🔥";
  if (score >= 50) return "🌿";
  return "🌱";
};

const MatchResultCard = ({ match, onAssign, profileTitle, availabilityDate, activeContract, profileLoaded }: CardProps) => {
  const [activeTab, setActiveTab] = useState<"domain" | "skills">("domain");
  const score = match.matchScore != null ? Math.round(match.matchScore) : null;
  const allMatchingSkills = match.matchingSkills ?? [];
  const allNonMatchingSkills = match.nonMatchingSkills ?? [];
  const skillsWeight = match.skillsWeight ?? 50;
  const domainWeight = 100 - skillsWeight;

  const domainBarWidth = (match.domainScore ?? 0) * (domainWeight / 100);
  const skillsBarWidth = (match.skillsScore ?? 0) * (skillsWeight / 100);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col gap-3">
      {/* Header row */}
      <div className="flex items-center gap-3">
        {match.profilePhotoId ? (
          <Image
            src={getImageUrl(match.profilePhotoId)!}
            className="rounded-xl w-12 h-12 flex-shrink-0 object-cover"
            alt={match.profileName ?? "Profile"}
          />
        ) : (
          <div className="rounded-xl w-12 h-12 flex-shrink-0 bg-gray-100 flex items-center justify-center text-gray-400 text-base font-semibold">
            {match.profileName?.[0] ?? "?"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm leading-tight truncate">
            {match.profileName ?? "Unknown"}
          </p>
          {profileTitle && (
            <p className="text-xs text-gray-400 truncate">{profileTitle}</p>
          )}
        </div>
        {profileLoaded && (
          activeContract
            ? <ContractTag activeContract={activeContract} />
            : <AvailabilityTag availabilityDate={availabilityDate} compact />
        )}
        <Button variant="secondary" onClick={onAssign} className="shrink-0">
          Create profile version
        </Button>
      </div>

      {/* Body: two panels */}
      <div className="flex gap-10">
        {/* Left panel */}
        <div className="flex flex-col gap-3 w-[45%] shrink-0">
          {/* Score label row + bar */}
          {score != null && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400 font-semibold">
                  Match score
                </p>
                <span className="flex items-center gap-1">
                  <span className="text-lg font-bold leading-none text-gray-900">
                    {score}
                  </span>
                  <span className="text-base">{scoreEmoji(score)}</span>
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
                <div
                  className="h-full bg-gray-900"
                  style={{ width: `${domainBarWidth}%` }}
                  title={`Domain: ${match.domainScore ?? 0}`}
                />
                <div
                  className="h-full bg-blue-500"
                  style={{ width: `${skillsBarWidth}%` }}
                  title={`Skills: ${match.skillsScore ?? 0}`}
                />
              </div>
            </div>
          )}

          {/* Assessment */}
          {match.finalAssessment && (
            <p className="text-sm text-gray-500 italic leading-snug">
              {match.finalAssessment}
            </p>
          )}
        </div>

        {/* Right panel */}
        <div className="flex flex-col gap-3 flex-1 min-w-0 bg-gray-100 rounded-2xl p-4">
          {/* Tab toggle */}
          <div className="flex items-center gap-1 text-xs">
            <button
              type="button"
              onClick={() => setActiveTab("domain")}
              className={cn(
                "flex items-center gap-1 px-3 py-1 rounded-full transition-colors",
                activeTab === "domain"
                  ? "bg-gray-100 font-semibold text-gray-900"
                  : "text-gray-400",
              )}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-900" />
              Domain
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("skills")}
              className={cn(
                "flex items-center gap-1 px-3 py-1 rounded-full transition-colors",
                activeTab === "skills"
                  ? "bg-gray-100 font-semibold text-gray-900"
                  : "text-gray-400",
              )}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />
              Skills
            </button>
          </div>

          {/* Tab content — fixed height to prevent card resize on tab switch */}
          <div className="relative h-48">
            <div className="h-full overflow-y-auto">
              {/* Domain breakdown */}
              {activeTab === "domain" && match.domainScores && (
                <div className="flex flex-col gap-4">
                  {match.domainScores.map((ds, i) => (
                    <TooltipProvider key={i} delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex flex-col gap-0.5 text-xs cursor-default">
                          <span className="text-gray-500 truncate">
                            {ds.domain}
                          </span>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gray-900 rounded-full"
                              style={{
                                width: `${((ds.score_0_to_5 ?? 0) / 5) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      </TooltipTrigger>
                      {ds.evidence && (
                        <TooltipContent className="max-w-xs text-xs">
                          {ds.evidence}
                        </TooltipContent>
                      )}
                    </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              )}

              {/* Skills breakdown */}
              {activeTab === "skills" && (
                <div className="flex flex-col gap-2">
                  {match.skillsSummary && (
                    <p className="text-sm text-gray-500 italic leading-snug">
                      {match.skillsSummary}
                    </p>
                  )}
                  {allMatchingSkills.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                      {allMatchingSkills.map((skill, i) => (
                        <EditorSkillTagButton
                          key={i}
                          text={skill.name}
                          handleClick={() => {}}
                          isStatic
                        />
                      ))}
                    </div>
                  )}
                  {allNonMatchingSkills.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] text-gray-400">Missing</span>
                      <div className="flex flex-wrap items-center gap-1">
                        {allNonMatchingSkills.map((skill, i) => (
                          <span
                            key={i}
                            className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-400 border border-red-200"
                          >
                            {skill.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SemanticMatchResultsList.displayName = "SemanticMatchResultsList";

interface OrgMemberCardProps {
  profile: UserProfile;
  onAssign: () => void;
  onMatch?: () => void;
  isMatching?: boolean;
}

const OrgMemberCard = ({
  profile,
  onAssign,
  onMatch,
  isMatching,
}: OrgMemberCardProps) => {
  const name =
    typeof profile.profileData.name === "string"
      ? profile.profileData.name
      : "Unknown";
  const title =
    typeof profile.profileData.title === "string"
      ? profile.profileData.title
      : undefined;

  return (
    <div
      className="aspect-square border border-gray-200 rounded-2xl p-4 flex flex-col justify-between"
      style={{ backgroundColor: "#F8F9FA" }}
    >
      <div className="flex flex-col items-center flex-1 justify-center">
        {profile.profilePhotoId ? (
          <Image
            src={getImageUrl(profile.profilePhotoId)!}
            className="rounded-xl w-16 h-16 flex-shrink-0 object-cover"
            alt={name}
          />
        ) : (
          <div className="rounded-xl w-16 h-16 flex-shrink-0 bg-gray-100 flex items-center justify-center text-gray-400 text-lg font-semibold">
            {name[0] ?? "?"}
          </div>
        )}
        <div className="text-center mt-1">
          <p className="font-semibold text-base leading-tight">{name}</p>
          <p className="text-sm text-gray-500">{title ?? ""}</p>
        </div>
      </div>
      <div className="flex flex-col gap-2 mt-4">
        {onMatch && (
          <Button
            variant="magic"
            className="w-full"
            onClick={onMatch}
            disabled={isMatching}
          >
            {isMatching ? "Matching…" : "Run match"}
          </Button>
        )}
        <Button variant="secondary" className="w-full" onClick={onAssign}>
          Create Lead Profile
        </Button>
      </div>
    </div>
  );
};
